/**
 * appraisalService.ts
 *
 * Step 5 — Performance Appraisal sign-off. Implements CLAUDE.md §G:
 *   - Employee + manager sign with timestamp + audit metadata (§G.1)
 *   - Employee may sign with a disagreement comment that unlocks Appeal (§G.2)
 *   - Sign-off never erases calibration history
 *
 * Manager notes and the development plan are editable while status is
 * 'draft' or 'sent_to_employee'. After 'manager_signed' the appraisal is
 * immutable except via the Appeal path.
 */

import {
  doc, getDoc, getDocs, query, where, collection,
  serverTimestamp, runTransaction, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  AppraisalForm, AppraisalStatus, SignatureRecord, DevPlanItem,
} from '../types/appraisal';
import { issueAppraisalSignTokens, issueResultViewTokens } from './magicLinkService';
import type { CycleMagicLinkExpiry } from '../types/magicLinkToken';
import { omitUndefinedDeep } from './firestoreUtils';

const COL = 'appraisalForms';

// ─── Errors ────────────────────────────────────────────────────────

export class AppraisalError extends Error {
  constructor(
    public code: 'not_found' | 'wrong_state' | 'unauthorized' | 'invalid',
    message: string,
  ) {
    super(message);
    this.name = 'AppraisalError';
  }
}

// ─── Read ──────────────────────────────────────────────────────────

export async function getAppraisal(id: string): Promise<AppraisalForm | null> {
  const snap = await getDoc(doc(db, COL, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<AppraisalForm, 'id'>) };
}

export async function listAppraisalsForCycle(cycleId: string): Promise<AppraisalForm[]> {
  const q = query(collection(db, COL), where('cycleId', '==', cycleId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<AppraisalForm, 'id'>) }));
}

// ─── Manager notes / dev plan ──────────────────────────────────────

export async function updateManagerNotes(id: string, notes: string): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppraisalError('not_found', 'Appraisal not found.');
    const a = snap.data() as AppraisalForm;
    if (a.status === 'manager_signed' || a.status === 'closed') {
      throw new AppraisalError('wrong_state', 'Appraisal already signed by manager.');
    }
    tx.update(ref, { managerCombinedNotes: notes, updatedAt: serverTimestamp() });
  });
}

export async function setDevelopmentPlan(id: string, plan: DevPlanItem[]): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppraisalError('not_found', 'Appraisal not found.');
    const a = snap.data() as AppraisalForm;
    if (a.status === 'manager_signed' || a.status === 'closed') {
      throw new AppraisalError('wrong_state', 'Appraisal already signed by manager.');
    }
    tx.update(ref, { developmentPlan: plan, updatedAt: serverTimestamp() });
  });
}

// ─── Send to employee (issues sign tokens + view tokens) ──────────

export interface SendToEmployeeInput {
  appraisalId: string;
  cycleId: string;
  employee: { id: string; email: string; name?: string };
  manager:  { id: string; email: string; name?: string };
  cycleExpiryConfig?: CycleMagicLinkExpiry;
}

export interface SendToEmployeeResult {
  signEmployee: string;       // url
  signManager:  string;       // url
  viewEmployee?: string;      // url
}

/**
 * Move the appraisal to 'sent_to_employee' and issue sign-off magic links
 * for both employee and manager. Also issues an employee result-view link
 * (reusable for 30 days).
 */
export async function sendToEmployee(input: SendToEmployeeInput): Promise<SendToEmployeeResult> {
  const ref = doc(db, COL, input.appraisalId);
  await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppraisalError('not_found', 'Appraisal not found.');
    const a = snap.data() as AppraisalForm;
    if (a.status !== 'draft') {
      throw new AppraisalError('wrong_state', `Cannot send from state '${a.status}'.`);
    }
    tx.update(ref, {
      status: 'sent_to_employee' as AppraisalStatus,
      updatedAt: serverTimestamp(),
    });
  });

  const sign = await issueAppraisalSignTokens({
    cycleId: input.cycleId,
    appraisalId: input.appraisalId,
    cycleExpiryConfig: input.cycleExpiryConfig,
    employee: input.employee,
    manager: input.manager,
  });
  const view = await issueResultViewTokens({
    cycleId: input.cycleId,
    cycleExpiryConfig: input.cycleExpiryConfig,
    recipients: [{
      recipientId:    input.employee.id,
      recipientEmail: input.employee.email,
      recipientName:  input.employee.name,
      recipientRole:  'employee',
      appraisalId:    input.appraisalId,
    }],
  });

  return {
    signEmployee:  sign.employee.url,
    signManager:   sign.manager.url,
    viewEmployee:  view[0]?.url,
  };
}

// ─── Sign-off (§G.1, §G.2) ─────────────────────────────────────────

export interface SignInput {
  appraisalId: string;
  signedBy: string;
  signedByName: string;
  signatureData: string;          // canvas dataURL or typed-name representation
  ipAddress: string;
  userAgent: string;
  magicLinkTokenId?: string;
}

export interface EmployeeSignInput extends SignInput {
  disagreeComment?: string;       // §G.2 — sets up appeal path when present
}

export async function signByEmployee(input: EmployeeSignInput): Promise<AppraisalForm> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, input.appraisalId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppraisalError('not_found', 'Appraisal not found.');
    const a = snap.data() as AppraisalForm;

    if (a.employeeId !== input.signedBy) {
      throw new AppraisalError('unauthorized', 'Not the assigned employee.');
    }
    if (a.status !== 'sent_to_employee') {
      throw new AppraisalError('wrong_state', `Cannot sign as employee from '${a.status}'.`);
    }

    const sig: SignatureRecord = {
      signedBy:        input.signedBy,
      signedByName:    input.signedByName,
      signedAt:        serverTimestamp() as unknown as Timestamp,
      ipAddress:       input.ipAddress,
      userAgent:       input.userAgent,
      magicLinkTokenId: input.magicLinkTokenId,
      signatureData:   input.signatureData,
    };

    // omitUndefinedDeep strips optional fields (magicLinkTokenId is the
    // common one — only set on magic-link signs, undefined for HR-side
    // direct signs). Without this, Firestore rejected the entire tx
    // with "Unsupported field value: undefined".
    const payload = omitUndefinedDeep({
      employeeSignature: sig,
      ...(input.disagreeComment?.trim()
          ? { employeeDisagreeComment: input.disagreeComment.trim() }
          : {}),
      status: 'employee_signed' as AppraisalStatus,
      updatedAt: serverTimestamp(),
    });
    tx.update(ref, payload);
    return { ...a, id: input.appraisalId, employeeSignature: sig, status: 'employee_signed' };
  });
}

export async function signByManager(input: SignInput): Promise<AppraisalForm> {
  return runTransaction(db, async tx => {
    const ref = doc(db, COL, input.appraisalId);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppraisalError('not_found', 'Appraisal not found.');
    const a = snap.data() as AppraisalForm;

    if (a.supervisorId !== input.signedBy) {
      throw new AppraisalError('unauthorized', 'Not the assigned supervisor.');
    }
    if (a.status !== 'employee_signed') {
      throw new AppraisalError(
        'wrong_state',
        `Manager can sign only after employee has signed (current: '${a.status}').`,
      );
    }

    const sig: SignatureRecord = {
      signedBy:        input.signedBy,
      signedByName:    input.signedByName,
      signedAt:        serverTimestamp() as unknown as Timestamp,
      ipAddress:       input.ipAddress,
      userAgent:       input.userAgent,
      magicLinkTokenId: input.magicLinkTokenId,
      signatureData:   input.signatureData,
    };

    // omitUndefinedDeep strips optional fields (magicLinkTokenId in
    // particular) — see signByEmployee for the failure class this
    // guards against.
    const payload = omitUndefinedDeep({
      managerSignature: sig,
      status: 'manager_signed' as AppraisalStatus,
      updatedAt: serverTimestamp(),
    });
    tx.update(ref, payload);
    return { ...a, id: input.appraisalId, managerSignature: sig, status: 'manager_signed' };
  });
}

// ─── Close (Step 5 → Step 6 entry) ─────────────────────────────────

/**
 * Mark the appraisal as closed and stamp the cycle into Step 6. From here
 * the 2-day appeal window starts ticking. Auto-advance to Step 7 happens
 * via the scheduled stepAdvancer when the window closes without an appeal
 * being filed (see appealService / scheduled function).
 */
export async function closeAppraisal(id: string): Promise<void> {
  await runTransaction(db, async tx => {
    const ref = doc(db, COL, id);
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new AppraisalError('not_found', 'Appraisal not found.');
    const a = snap.data() as AppraisalForm;
    if (a.status !== 'manager_signed') {
      throw new AppraisalError('wrong_state', `Cannot close from '${a.status}'.`);
    }
    tx.update(ref, { status: 'closed' as AppraisalStatus, updatedAt: serverTimestamp() });
  });
  // Cycle-level cursor flip handled by caller / advancer in C8.
}
